import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { shouldUsePinMode } from '@/utils/pinModeUtils';
import { PixelCharacter } from './PixelCharacter';
import './pixel-animations.css';

interface ActiveShift {
  entry_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  avatar_pose: string;
  avatar_description: string | null;
  project_name: string;
  start_time: string;
  duration_minutes: number | null;
}

export const ActiveCrewDisplay: React.FC = () => {
  const [shifts, setShifts] = useState<ActiveShift[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActiveShifts();
    const interval = setInterval(fetchActiveShifts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchActiveShifts = async () => {
    try {
      if (!shouldUsePinMode()) {
        setLoading(false);
        return;
      }

      const pin = localStorage.getItem('manager_pin') || localStorage.getItem('employee_pin') || '';
      if (!pin) { setLoading(false); return; }

      const { data, error } = await supabase.rpc('pin_get_active_shifts', { p_pin: pin });
      if (error) throw error;
      setShifts((data as ActiveShift[]) || []);
    } catch (err) {
      console.error('Erreur chargement shifts actifs:', err);
    } finally {
      setLoading(false);
    }
  };

  const getElapsedTime = (startTime: string) => {
    const start = new Date(startTime);
    const now = new Date();
    const diffMin = Math.floor((now.getTime() - start.getTime()) / 60000);
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    return `${h}h${String(m).padStart(2, '0')}`;
  };

  if (loading) return null;
  if (shifts.length === 0) return null;

  return (
    <div className="rounded-xl border bg-white/80 overflow-hidden px-4 py-4">
        <div className="flex flex-wrap gap-4 justify-center">
          {shifts.map((shift, index) => (
            <div
              key={shift.entry_id}
              className="flex flex-col items-center text-center"
              style={{ minWidth: '100px', maxWidth: '120px' }}
            >
              <PixelCharacter
                pose={shift.avatar_pose || 'hammer'}
                colorIndex={index}
                size={80}
              />
              <div className="mt-1 text-gray-900 text-xs font-semibold truncate w-full">
                {shift.first_name} {shift.last_name?.charAt(0)}.
              </div>
              <div className="text-blue-600 text-[10px] truncate w-full">
                {shift.project_name}
              </div>
              <div className="text-emerald-600 text-[10px] font-mono">
                ⏱ {getElapsedTime(shift.start_time)}
              </div>
              {shift.avatar_description && (
                <div className="text-gray-400 text-[9px] italic truncate w-full mt-0.5">
                  "{shift.avatar_description}"
                </div>
              )}
            </div>
          ))}
        </div>
    </div>
  );
};
